import { useEffect, useState, useRef, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { MenuItem, SelectChangeEvent } from "@mui/material"
import moment from "moment"
import { useSearchParams } from "@/hooks/useSearchParams"

import Selector from "@/components/form/selector/Selector"
import Chip from "@/components/chip/Chip"
import Checkbox from "@/components/form/checkbox/Checkbox"
import { getSingleItemDuration, getWholeItemDuration } from "@/utils/format"
import { CheckboxColor } from "@/types/components/checkbox"
import { TIME_FORMAT } from "@/constants/date"
import { ACTIVITY_SESSION_TYPE } from "@/app/report/common/constants"
import { Session } from "@/types/session"
import BasicButton from "@/components/button/BasicButton"

import "@/app/report/components/filter/reportFilter.scss"

export default function ReportSectionSessionSelector({
  open,
  setOpen,
  selectedSessionList,
  setSelectedSessionList,
  sessionList,
}: {
  open: boolean
  setOpen: (open: boolean) => void
  selectedSessionList: number[]
  setSelectedSessionList: (selectedSessionList: number[]) => void
  sessionList?: Session[]
}) {
  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const containerRef = useRef<HTMLDivElement>(null)

  const dailyReportType = searchParams.get("dailyReportType") || ACTIVITY_SESSION_TYPE.SESSION

  const [tempSelectedSessionList, setTempSelectedSessionList] = useState<number[]>([])

  const isAllSessionsSelected = useMemo(() => {
    return (sessionList?.length ?? 0) > 0 && tempSelectedSessionList.length === sessionList?.length
  }, [sessionList, tempSelectedSessionList])

  const handleSelectAllSessions = (checked: boolean) => {
    if (checked && sessionList) {
      setTempSelectedSessionList(sessionList.map((s) => s.idfSession))
    } else {
      setTempSelectedSessionList([])
    }
  }

  useEffect(() => {
    setTempSelectedSessionList(selectedSessionList)
  }, [selectedSessionList])

  // 스크롤 시 Menu 닫기 (메뉴 내부 스크롤은 제외)
  useEffect(() => {
    if (!open) return

    const handleScroll = (e: Event) => {
      // 메뉴 내부 스크롤이면 닫지 않음
      const menuElement = document.querySelector(".report-session-filter-menu")
      if (menuElement?.contains(e.target as Node)) {
        return
      }
      setOpen(false)
    }

    window.addEventListener("scroll", handleScroll, true)
    return () => {
      window.removeEventListener("scroll", handleScroll, true)
    }
  }, [open, setOpen])

  return (
    <div className="report-session-filter" ref={containerRef}>
      <Selector
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false)
        }}
        type="select"
        MenuProps={{
          disablePortal: false,
          PaperProps: {
            className: "report-session-filter-menu",
            style: { width: containerRef.current?.offsetWidth },
          },
        }}
        value={dailyReportType}
        onChange={(e: SelectChangeEvent<string>) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(true)
        }}
        renderValue={(value) => {
          if (value === ACTIVITY_SESSION_TYPE.ACTIVITY) {
            return t(value)
          }

          return (
            <span className="report-filter-selector-value">
              {t(value)}
              <Chip type="number" label={tempSelectedSessionList.length + ""} />
            </span>
          )
        }}
        disabled={dailyReportType === ACTIVITY_SESSION_TYPE.ACTIVITY}>
        {!!sessionList?.length && (
          <MenuItem
            key={ACTIVITY_SESSION_TYPE.SESSION}
            value={ACTIVITY_SESSION_TYPE.SESSION}
            className="report-session-filter-session-item"
            disableRipple>
            <div className="report-session-filter-selector-group">
              <Checkbox
                className="report-session-filter-checkbox-all"
                value="session"
                label={
                  <div className="session-radio-label">
                    <p>{t("session")}</p>
                    <p>{getWholeItemDuration(sessionList)}</p>
                  </div>
                }
                checked={isAllSessionsSelected}
                indeterminate={tempSelectedSessionList.length > 0 && !isAllSessionsSelected}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setOpen(true)
                  handleSelectAllSessions(!isAllSessionsSelected)
                }}
                color={CheckboxColor.PLATINUM}
              />

              <div
                className="report-session-filter-selector-checkbox"
                style={{ maxHeight: "400px", overflowY: "auto" }}>
                {sessionList.map((session) => (
                  <MenuItem
                    component="div"
                    key={session.idfSession}
                    className="session-checkbox-item"
                    onClick={(e) => {
                      setOpen(true)
                      e.preventDefault()
                      e.stopPropagation()
                      if (tempSelectedSessionList.includes(session.idfSession)) {
                        setTempSelectedSessionList(
                          tempSelectedSessionList.filter((id) => id !== session.idfSession),
                        )
                      } else {
                        setTempSelectedSessionList([...tempSelectedSessionList, session.idfSession])
                      }
                    }}>
                    <Checkbox
                      checked={tempSelectedSessionList.includes(session.idfSession)}
                      value={session.idfSession}
                      onClick={(e) => {
                        setOpen(true)
                        e.preventDefault()
                        e.stopPropagation()
                        if (tempSelectedSessionList.includes(session.idfSession)) {
                          setTempSelectedSessionList(
                            tempSelectedSessionList.filter((id) => id !== session.idfSession),
                          )
                        } else {
                          setTempSelectedSessionList([
                            ...tempSelectedSessionList,
                            session.idfSession,
                          ])
                        }
                      }}
                    />
                    <div className="session-checkbox-text">
                      <div className="session-checkbox-name-wrapper">
                        <p className="session-checkbox-name">{session.name}</p>
                        <p className="session-checkbox-duration">
                          {getSingleItemDuration(session)}
                        </p>
                      </div>
                      <p className="session-checkbox-time">
                        {moment(session.dateStart).format(TIME_FORMAT)} ~{" "}
                        {moment(session.dateFinish).format(TIME_FORMAT)}
                      </p>
                    </div>
                  </MenuItem>
                ))}
              </div>
            </div>
          </MenuItem>
        )}
        <div className="session-apply-button">
          <BasicButton
            type="primary-contained"
            size="sm"
            onClick={() => {
              setSelectedSessionList(tempSelectedSessionList)
              setOpen(false)
            }}
            disabled={
              selectedSessionList === tempSelectedSessionList || !tempSelectedSessionList.length
            }>
            {t("button.apply")}
          </BasicButton>
        </div>
      </Selector>
    </div>
  )
}
